import {
  DraftId,
  PresetId,
  PresetVersionId,
  Sha256,
  Timestamp,
  UserId,
} from "@pax/api-contract/common";
import {
  DraftPage,
  DraftProblem,
  DraftTeam,
  PresetPermission,
  VersionNumber,
} from "@pax/api-contract/presets/v1alpha1";
import { StartDateSchema } from "@pax/world";
import { z } from "zod";

export { DraftId, DraftPage, DraftProblem };

export const DraftIdParams = z
  .strictObject({ id: DraftId })
  .meta({ id: "DraftIdParams", description: "A route parameter naming one draft." });
export type DraftIdParams = z.infer<typeof DraftIdParams>;

export const DraftRevision = z.number().int().nonnegative().meta({
  id: "DraftRevision",
  description: "How many changes a draft has taken since it began. Each one is the next revision.",
  example: 1400,
});
export type DraftRevision = z.infer<typeof DraftRevision>;

export const DraftConnection = z
  .strictObject({
    token: z.string().min(1).meta({
      description: "Send it in the first socket frame as `authenticate.token`.",
    }),
    expiresAt: Timestamp.meta({
      description: "When the token stops working. Connect again after.",
    }),
    userId: UserId,
  })
  .meta({ id: "DraftConnection", description: "A short-lived draft token, and who it's for." });
export type DraftConnection = z.infer<typeof DraftConnection>;

/** A file in the catalog, by its digest. */
const CatalogBlob = z.strictObject({ digest: Sha256, bytes: z.number().int().positive() });

/** The published version a draft began from: its World artifact, and the records its draft held. */
export const DraftBase = z
  .strictObject({
    versionId: PresetVersionId,
    world: CatalogBlob,
    records: CatalogBlob.meta({
      description: "The version's records file, which the draft begins with.",
    }),
  })
  .meta({
    id: "DraftBase",
    description:
      "The published version a draft began from, and the digests of its World artifact and its records.",
  });
export type DraftBase = z.infer<typeof DraftBase>;

export const CreateDraftRequest = z
  .strictObject({
    presetId: PresetId,
    base: DraftBase.nullable().meta({
      description: "The published version it begins from, or null to begin blank.",
    }),
    page: DraftPage.meta({ description: "What its page says as it begins: its `page:main`." }),
  })
  .meta({ id: "CreateDraftRequest", description: "Where a new draft begins." });
export type CreateDraftRequest = z.infer<typeof CreateDraftRequest>;

/** Someone who may open a draft, and what their roles let them change in its world. */
export const DraftTeamMember = z
  .strictObject({ userId: UserId, permissions: z.array(PresetPermission) })
  .meta({ id: "DraftTeamMember" });
export type DraftTeamMember = z.infer<typeof DraftTeamMember>;

export const SetDraftTeamRequest = z
  .strictObject({
    members: z.array(DraftTeamMember),
    team: DraftTeam.meta({
      description:
        "Its owner, roles and members' roles, which say what each may do with each item.",
    }),
  })
  .meta({
    id: "SetDraftTeamRequest",
    description:
      "Everyone on the preset's team now, the owner included. Anyone left out is closed out of the draft.",
  });
export type SetDraftTeamRequest = z.infer<typeof SetDraftTeamRequest>;

/**
 * A draft compiled into a World artifact and the records a draft from its version begins with,
 * each stored as a blob, and what its version will say.
 */
export const CompiledDraft = z
  .strictObject({
    revision: DraftRevision.meta({ description: "The draft's revision it was compiled at." }),
    page: DraftPage.meta({ description: "Its page at that revision." }),
    world: CatalogBlob.extend({
      throughRevision: z.number().int().positive().meta({
        description: "How many deltas the World artifact holds.",
      }),
    }),
    records: CatalogBlob.meta({
      description: "Everything the draft holds but its page, as a records file.",
    }),
    regionCount: z.number().int().nonnegative(),
    entityCount: z.number().int().nonnegative(),
    startDate: StartDateSchema,
  })
  .meta({ id: "CompiledDraft" });
export type CompiledDraft = z.infer<typeof CompiledDraft>;

export const ThawDraftRequest = z
  .strictObject({ problems: z.array(DraftProblem) })
  .meta({ id: "ThawDraftRequest", description: "Why its publish was refused or failed." });
export type ThawDraftRequest = z.infer<typeof ThawDraftRequest>;

/** The version a draft was published as: its place among the preset's versions, and its number. */
export const PublishedAs = z
  .strictObject({ version: z.number().int().positive(), versionNumber: VersionNumber })
  .meta({ id: "PublishedAs", description: "The version the draft was published as." });
export type PublishedAs = z.infer<typeof PublishedAs>;

export const RetireDraftRequest = PublishedAs.meta({ id: "RetireDraftRequest" });
export type RetireDraftRequest = z.infer<typeof RetireDraftRequest>;

export const DraftSessionState = z
  .strictObject({ revision: DraftRevision, frozen: z.boolean() })
  .meta({ id: "DraftSessionState", description: "Where a draft session stands." });
export type DraftSessionState = z.infer<typeof DraftSessionState>;
